import { useTranslation } from 'react-i18next'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Autocomplete, Button, TextField, Typography, Box } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'

import {
  getContentUploadIntegrations,
  getProviderIntegration,
} from '@/routes/Integration/integrationHelpers'
import { getIntegrationCourses } from '@/store/quizzes/actions'
import integrationService from '@/services/IntegrationService'
import { HelperService } from '@/services/helper.service'
import { capitalizeFirstLetter } from '@/utils/appUtils'
import { localDateTime } from '@/utils/formatUtils'
import CustomSelect from '@/components/UI/Select'
import ChildPageHeader from '@/components/UI/ChildPageHeader'

const helperService = new HelperService()

export interface ImportDocumentRef {
  onClickImport: () => void
}

interface Course {
  display_name: string
  id: string
  label: string
  name: string
  value: string
}

interface AsyncTask {
  _id: string
  organisation_id: string
  agent_id: string
  author_id: string
  task_id: string
  status: string | null
  created_at: string
  updated_at: string
  task_data?: {
    organisation_id: string
    agent_id: string
    integration_id: string
    lms_course_id: string
  }
}

const ImportLmsContent = React.forwardRef<ImportDocumentRef, any>(
  (props: any, _ref) => {
    const { t } = useTranslation('translation')
    const dispatch = useDispatch()
    const [selectedSource, setSelectedSource] = useState<{
      label: string
      value: string
    } | null>(null)
    const [loading, setLoading] = useState(false)
    const [sourceOptions, setSourceOptions] = useState([])
    const [courses, setCourses] = useState<Course[]>([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedCourseId, setSelectedCourseId] = useState('')
    const [isAuthenticated, setIsAuthenticated] = useState(true)
    const [contentIntegrations, setContentIntegrations] = useState<{
      [key: string]: { label: string; value: string }[]
    }>({})
    const [selectedContentIntegration, setSelectedContentIntegration] =
      useState<{
        label: string
        value: string
      }>({
        label: '',
        value: 'current',
      })
    const [asyncTasks, setAsyncTasks] = useState<AsyncTask[]>([])
    const [tasksLoading, setTasksLoading] = useState(false)
    const [paginationModel, setPaginationModel] = useState({
      page: 0,
      pageSize: 10,
    })
    const [totalTasks, setTotalTasks] = useState(0)
    const [abortController, setAbortController] =
      useState<AbortController | null>(null)
    const [btnClicked, setBtnClicked] = useState(false)
    const [integrationMap, setIntegrationMap] = useState<{
      [key: string]: { provider: string; name: string }
    }>({})

    let integrate = true
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const sourceLabels: { [index: string]: any } = {
      canvas: 'Canvas',
      blackboard: 'Blackboard',
      desire2learn: 'D2L Brightspace',
      moodle: 'Moodle',
    }

    const asyncTasksColumns: GridColDef[] = [
      {
        field: 'status',
        headerName: t('Status'),
        flex: 0.8,
        minWidth: 120,
        renderCell: params => {
          if (params.value === 'completed') {
            return t('Completed')
          } else if (params.value === 'failed') {
            return t('Failed')
          } else {
            return t('Pending')
          }
        },
      },
      {
        field: 'lms_provider',
        headerName: t('Source'),
        flex: 1,
        minWidth: 150,
        renderCell: params => {
          const integrationId = params.row.task_data?.integration_id
          if (!integrationId || !integrationMap[integrationId]) {
            return '-'
          }
          const provider = integrationMap[integrationId].provider
          return sourceLabels[provider] || capitalizeFirstLetter(provider)
        },
      },
      {
        field: 'course_id',
        headerName: t('Course ID'),
        flex: 1,
        minWidth: 120,
        renderCell: params => params.row.task_data?.lms_course_id || '-',
      },
      {
        field: 'created_at',
        headerName: t('Creation Date'),
        flex: 1,
        minWidth: 180,
        renderCell: params => localDateTime(params.value),
      },
      {
        field: 'updated_at',
        headerName: t('Last Updated'),
        flex: 1,
        minWidth: 180,
        renderCell: params => localDateTime(params.value),
      },
    ]

    const showContentIntegrations =
      selectedSource &&
      contentIntegrations.hasOwnProperty(selectedSource.value) &&
      contentIntegrations[selectedSource.value].length > 1

    useEffect(() => {
      fetchSourceOptions()
      fetchAsyncTasks()

      return () => {
        integrate = false
        timerRef.current && clearTimeout(timerRef.current)
      }
    }, [])

    useEffect(() => {
      fetchAsyncTasks()
    }, [paginationModel.page, paginationModel.pageSize])

    const fetchAsyncTasks = async () => {
      try {
        setTasksLoading(true)
        const response = await integrationService.getAsyncTasks(
          'lmsimport_task',
          paginationModel.page + 1, // API is 1-based, DataGrid is 0-based
          paginationModel.pageSize,
        )
        if (response?.data?.asynctasks) {
          setAsyncTasks(response.data.asynctasks)
          setTotalTasks(response.data.pagination?.total_items || 0)
        }
      } catch (error) {
        console.error('Failed to fetch async tasks', error)
      } finally {
        setTasksLoading(false)
      }
    }

    const handleCourseChange = (newValue: Course | null) => {
      setSelectedCourse(newValue)
      handleCourseIdChange(newValue ? newValue.id : '')
    }

    const handleCourseIdChange = (newCourseId: string) => {
      setSelectedCourseId(newCourseId)

      // If user is typing manually, abort any ongoing course fetch
      if (newCourseId && abortController) {
        abortController.abort()
        setAbortController(null)
        setLoading(false)
      }
    }

    const handleSourceChange = (newSource: any) => {
      setSelectedSource(newSource)
      setSelectedContentIntegration({
        label: '',
        value: 'current',
      })
      setCourses([])

      // Moodle doesn't require OAuth, so set authenticated to true
      if (newSource && newSource.value === 'moodle') {
        setIsAuthenticated(true)
      }

      if (
        !newSource ||
        !contentIntegrations.hasOwnProperty(newSource.value) ||
        contentIntegrations[newSource.value].length <= 1
      ) {
        handleSelectedSourceCourses(
          newSource.value,
          selectedContentIntegration.value,
        )
        handleCourseChange(null)
      }
    }

    const handleContentIntegrationChange = (newContentIntegration: any) => {
      setSelectedContentIntegration(newContentIntegration)
      setCourses([])

      // Moodle doesn't require OAuth, so set authenticated to true
      if (selectedSource && selectedSource.value === 'moodle') {
        setIsAuthenticated(true)
      }

      handleSelectedSourceCourses(
        selectedSource.value,
        newContentIntegration.value,
      )
      handleCourseChange(null)
    }

    const fetchSourceOptions = async () => {
      setLoading(true)
      const integrations = await getContentUploadIntegrations()

      // Only support Moodle and Canvas for now
      const supportedProviders = ['moodle', 'canvas']
      const filteredIntegrations = integrations.filter(
        (integration: { provider: string }) =>
          supportedProviders.includes(integration.provider),
      )

      let _sourceOptions: { [key: string]: { label: string; value: string } } =
        {}
      let _contentIntegrations: {
        [key: string]: { label: string; value: string }[]
      } = {}
      let _integrationMap: {
        [key: string]: { provider: string; name: string }
      } = {}
      filteredIntegrations.forEach(
        (integration: { provider: string; name: string; _id: string }) => {
          if (!_sourceOptions.hasOwnProperty(integration.provider)) {
            _sourceOptions[integration.provider] = {
              label: sourceLabels.hasOwnProperty(integration.provider)
                ? sourceLabels[integration.provider]
                : capitalizeFirstLetter(integration.provider),
              value: integration.provider,
            }
          }

          if (!_contentIntegrations.hasOwnProperty(integration.provider)) {
            _contentIntegrations[integration.provider] = []
          }
          _contentIntegrations[integration.provider].push({
            label: integration.name,
            value: integration._id,
          })

          // Build integration map for async tasks display
          _integrationMap[integration._id] = {
            provider: integration.provider,
            name: integration.name,
          }
        },
      )

      let __sourceOptions = Object.values(_sourceOptions)
      setSourceOptions(__sourceOptions)
      setContentIntegrations(_contentIntegrations)
      setIntegrationMap(_integrationMap)
      setLoading(false)
      if (__sourceOptions.length > 0) {
        handleSourceChange(__sourceOptions[0])
      }
    }

    const handleLoginToIntegrationProvider = async (provider: string) => {
      try {
        setLoading(true)
        const connectToIntegrationResponse = await getProviderIntegration(
          provider,
          !btnClicked,
          selectedContentIntegration.value,
        )

        if (
          connectToIntegrationResponse &&
          connectToIntegrationResponse.integrationData
        ) {
          if (
            connectToIntegrationResponse.integrationData.hasOwnProperty(
              `${provider}_integration`,
            ) &&
            connectToIntegrationResponse.integrationData[
              `${provider}_integration`
            ]
          ) {
            handleSelectedSourceCourses(
              provider,
              selectedContentIntegration.value,
            )
            return
          } else if (connectToIntegrationResponse.integrationData.auth_url) {
            if (!btnClicked) {
              window.open(
                connectToIntegrationResponse.integrationData.auth_url,
                '_blank',
              )
              setBtnClicked(true)
            }

            timerRef.current = setTimeout(() => {
              if (integrate) {
                handleLoginToIntegrationProvider(provider)
              }
            }, 5 * 1000)
          }
        }

        setLoading(false)
      } catch (error) {
        setBtnClicked(false)
        setLoading(false)
        setIsAuthenticated(false)
      }
    }

    const handleSelectedSourceCourses = async (
      provider: string,
      integrationId?: string,
    ) => {
      // Cancel any previous fetch request
      if (abortController) {
        abortController.abort()
      }

      // Create new abort controller for this request
      const newAbortController = new AbortController()
      setAbortController(newAbortController)

      try {
        setLoading(true)

        // Create a timeout promise that rejects after 10 seconds
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        })

        // Create an abort promise
        const abortPromise = new Promise((_, reject) => {
          newAbortController.signal.addEventListener('abort', () => {
            reject(new Error('Request aborted'))
          })
        })

        // Race between the API call, timeout, and abort
        const currentIntegrationCoursesResponse: any = await Promise.race([
          dispatch(getIntegrationCourses(provider, integrationId)),
          timeoutPromise,
          abortPromise,
        ])

        // Only update if not aborted
        if (!newAbortController.signal.aborted) {
          setCourses([
            ...currentIntegrationCoursesResponse.courses.map((d: any) => {
              return { ...d, label: d.display_name, value: d.id }
            }),
          ])
          setIsAuthenticated(true)
          setLoading(false)
          setAbortController(null)
        }
      } catch (error: any) {
        // Only handle errors if request wasn't aborted
        if (error.message !== 'Request aborted') {
          if (error && error.status && error.status.code === 400) {
            setIsAuthenticated(false)
          } else if (error.message === 'Request timeout') {
            console.log(
              'Request timed out. Please try again or enter the course ID manually.',
            )
          }
          setLoading(false)
          setAbortController(null)
        } else {
          // Request was aborted by user input
          setLoading(false)
        }
      }
    }

    useEffect(() => {
      const isDisabled = !selectedCourseId || loading
      props?.setIsImportButtonDisabled &&
        props.setIsImportButtonDisabled(isDisabled)
    }, [loading, selectedCourseId])

    return (
      <section className="build-knowledge-v2 page-mid-wraper h-without-foter">
        <ChildPageHeader
          title={t('Import Content from an LMS')}
          subTitle={t(
            'Choose an LMS and select a course to import content from your integrated platform',
          )}
        />
        <div className="integration-sources">
          <div className="select-course tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
            <div
              className={
                showContentIntegrations ? 'tw-col-span-3' : 'tw-col-span-6'
              }
            >
              <CustomSelect
                value={selectedSource?.value || ''}
                onChange={(_value, option) => handleSourceChange(option as any)}
                options={sourceOptions}
                placeholder={t('Select Source')}
              />
            </div>
            {showContentIntegrations && selectedSource && (
              <Autocomplete
                size="small"
                value={selectedContentIntegration}
                className="tw-col-span-3"
                onChange={(_event: any, newValue) => {
                  handleContentIntegrationChange(newValue)
                }}
                options={contentIntegrations[selectedSource.value]}
                renderInput={params => (
                  <TextField {...params} placeholder={t('Select Instance')} />
                )}
                placeholder={t('Select Instance')}
              />
            )}

            <div className="tw-col-span-6">
              <Autocomplete
                size="small"
                value={selectedCourse}
                defaultValue={null}
                freeSolo
                clearOnEscape
                onChange={(_event: any, newValue: Course | null | string) => {
                  if (typeof newValue === 'string') {
                    // User entered a custom value (course ID)
                    handleCourseIdChange(newValue)
                    setSelectedCourse(null)
                  } else {
                    handleCourseChange(newValue)
                  }
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === 'input') {
                    // Only update when user is typing (not when selecting from dropdown)
                    handleCourseIdChange(val)
                    setSelectedCourse(null)
                  } else if (reason === 'clear') {
                    handleCourseIdChange('')
                    setSelectedCourse(null)
                  }
                }}
                options={courses}
                getOptionLabel={option => {
                  if (typeof option === 'string') {
                    return option
                  }
                  return (
                    option.display_name || option.name || option.label || ''
                  )
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={t('Select or enter Course ID')}
                  />
                )}
              />
              <Typography
                variant="caption"
                className="tw-text-gray-500 tw-mt-1 tw-block"
              >
                {loading
                  ? t('Loading courses...')
                  : t(
                      'You can select from the list or type the course ID directly',
                    )}
              </Typography>
            </div>
          </div>

          {/* Authentication UI for providers that require OAuth */}
          {!isAuthenticated &&
            selectedSource &&
            selectedSource.value !== 'moodle' && (
              <div className="tw-flex tw-justify-center tw-items-center tw-my-6">
                <div className="tw-text-center">
                  <Typography variant="body1" className="tw-mb-4">
                    {t(
                      `Please log in to your ${selectedSource.label} account to proceed`,
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (selectedSource) {
                        handleLoginToIntegrationProvider(selectedSource.value)
                      }
                    }}
                    disabled={btnClicked}
                  >
                    {t(`Log in to ${selectedSource.label}`)}
                  </Button>
                </div>
              </div>
            )}

          {/* Only show import section when authenticated */}
          {isAuthenticated && (
            <div className="my-3">
              <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-3">
                <Typography
                  variant="body2"
                  component="p"
                  className="tw-text-sm tw-text-gray-600 tw-max-w-[760px] tw-mx-auto"
                >
                  {t(
                    'The import runs asynchronously and replicates your LMS course structure by creating knowledge groups for modules / sections and importing files into their corresponding sections.',
                  )}
                  <br />
                  {t(
                    'You will receive a notification once the task is complete.',
                  )}
                </Typography>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!selectedSource) return
                    try {
                      setLoading(true)
                      await integrationService.importCourseContent(
                        selectedSource.value,
                        selectedCourseId,
                        selectedContentIntegration?.value || 'current',
                      )
                      helperService.showToaster(
                        t(
                          'Import task created. You will receive a notification once the task is complete.',
                        ),
                        'success',
                      )
                      // Deselect course after creating task
                      setSelectedCourse(null)
                      setSelectedCourseId('')
                      // Refresh async tasks
                      fetchAsyncTasks()
                    } catch (e) {
                      // Optional: handle error state/logging
                      // console.error('Import failed', e)
                    } finally {
                      setLoading(false)
                    }
                  }}
                  disabled={!selectedCourseId || loading || !selectedSource}
                >
                  {t('Import Course Content')}
                </Button>
              </div>
            </div>
          )}

          {/* Async Tasks Table */}
          {totalTasks > 0 && (
            <>
              {/* Divider */}
              <div className="tw-border-t tw-border-gray-300 tw-my-8"></div>

              <div className="tw-mt-6">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <Typography variant="subtitle2" component="h2">
                    {t('Import History')}
                  </Typography>
                </div>
                <Box sx={{ height: 500, mt: 1 }}>
                  <DataGrid
                    rows={asyncTasks}
                    columns={asyncTasksColumns}
                    loading={tasksLoading}
                    getRowId={row => row._id}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    rowCount={totalTasks}
                    pageSizeOptions={[5, 10, 20, 50]}
                    disableRowSelectionOnClick
                  />
                </Box>
              </div>
            </>
          )}
        </div>
      </section>
    )
  },
)

export default ImportLmsContent
